/**
 * SEO Structured Data (JSON-LD) Schemas Generator
 *
 * Generates semantic structured data for Google and AI search bots.
 * Entities are linked via @id IRIs as recommended by seo-010.
 */

export interface Crumb {
  name: string;
  url: string;
}

/**
 * Builds the global Organization and WebSite schema for the landing page.
 */
export const buildOrganizationSchema = (baseUrl: string) => {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${baseUrl}/#organization`,
    name: "Moderate Politics",
    url: baseUrl,
    logo: `${baseUrl}/mp-icon.png`,
    sameAs: [
      "https://twitter.com/moderatepol",
      "https://www.facebook.com/moderatepolitics",
    ],
  };
};

/**
 * Builds a valid BreadcrumbList schema.
 */
export const buildBreadcrumbSchema = (crumbs: Crumb[], baseUrl: string) => {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((crumb, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: crumb.name,
      item: crumb.url.startsWith("http") ? crumb.url : `${baseUrl}${crumb.url}`,
    })),
  };
};

/**
 * Bounds Firebase room details to the DiscussionForumPosting schema shape.
 */
export interface RoomDataForSchema {
  pk: string | number;
  topic: string;
  host?: {
    name?: string;
    profile?: string;
  };
  createdAt?: string | number;
  messageCount?: number;
  city?: string;
  country?: string;
}

export const buildDiscussionPostingSchema = (
  room: RoomDataForSchema,
  baseUrl: string
) => {
  const roomUrl = `${baseUrl}/conversations-nearby/${room.pk}`;
  const authorName = room.host?.name || "Anonymous User";
  const publishedDate = room.createdAt
    ? new Date(room.createdAt).toISOString()
    : new Date().toISOString();

  return {
    "@context": "https://schema.org",
    "@type": "DiscussionForumPosting",
    "@id": `${roomUrl}/#discussion`,
    isPartOf: {
      "@type": "WebPage",
      "@id": roomUrl,
    },
    publisher: {
      "@id": `${baseUrl}/#organization`,
    },
    headline: room.topic,
    text: `Join the constructive political debate room: "${room.topic}" created by ${authorName}. Discuss politics, share views, and connect with people.`,
    author: {
      "@type": "Person",
      name: authorName,
    },
    datePublished: publishedDate,
    dateModified: publishedDate,
    interactionStatistic: {
      "@type": "InteractionCounter",
      interactionType: "https://schema.org/CommentAction",
      userInteractionCount: room.messageCount || 0,
    },
  };
};

/**
 * Builds a Person schema.
 */
export const buildPersonSchema = (user: any, baseUrl: string) => {
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": `${baseUrl}/profile/${user.pk || ""}#person`,
    name: user.name || "Anonymous User",
    url: `${baseUrl}/profile/${user.pk || ""}`,
    image: user.profile ? `${baseUrl}${user.profile}` : undefined,
  };
};
